import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChefHat, ShieldCheck, Phone, ArrowLeft } from "lucide-react";
import { BrandMark } from "@/components/Brand";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function GoogleButton({ onClick, busy }) {
  return (
    <Button
      type="button" variant="outline" onClick={onClick} disabled={busy}
      className="w-full rounded-full h-12 border-2 hover:border-primary/40 hover:bg-secondary/5"
      data-testid="google-signin-btn"
    >
      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      Continue with Google
    </Button>
  );
}

export default function AuthPage() {
  const { sendOtp, verifyOtp, login, user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Step starts at phone OTP for everyone. Admin can toggle to email+password.
  // Legacy ?as=admin URLs still work — they jump straight to the admin form.
  const [step, setStep] = useState(params.get("as") === "admin" ? "adminLegacy" : "phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [keepLogin, setKeepLogin] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Admin legacy login
  const [adminEmail, setAdminEmail] = useState("admin@delcocefs.in");
  const [adminPw, setAdminPw] = useState("");

  useEffect(() => {
    if (!loading && user) {
      if (user.role === "admin") nav("/admin", { replace: true });
      else if (user.role === "chef") nav("/chef", { replace: true });
      else nav(user.onboarded ? "/app" : "/onboarding", { replace: true });
    }
  }, [user, loading, nav]);

  useEffect(() => {
    if (cooldown > 0) {
      const t = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [cooldown]);

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!/^[+\d\s-]{10,15}$/.test(phone.trim())) {
      toast.error("Enter a valid Indian mobile number");
      return;
    }
    setBusy(true);
    const res = await sendOtp(phone.trim());
    setBusy(false);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success("OTP sent to your phone");
    setCooldown(30);
    setStep("otp");
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy(true);
    const res = await verifyOtp({
      mobile: phone.trim(), otp,
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
    });
    setBusy(false);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success(`Welcome${res.user.name ? `, ${res.user.name.split(" ")[0]}` : ""}!`);
    const u = res.user;
    if (u.role === "admin") nav("/admin");
    else if (u.role === "chef") nav("/chef");
    else nav(u.onboarded ? "/app" : "/onboarding");
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(adminEmail, adminPw);
    setBusy(false);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success("Signed in");
    nav("/admin");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[hsl(var(--muted))]">
        <img src="https://images.pexels.com/photos/5792480/pexels-photo-5792480.jpeg" className="absolute inset-0 w-full h-full object-cover opacity-85" alt="ingredients" />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsla(20,3%,17%,0.45)] to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 flex items-center justify-center"><BrandMark size={36}/></div>
          </Link>
          <div>
            <p className="heading text-3xl font-semibold max-w-md leading-tight">
              Home-cooked meals, on your schedule. Sign in with phone or Google.
            </p>
            <p className="text-sm mt-3 max-w-md opacity-90">
              No passwords. One-time OTP only, every device, every time.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md space-y-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="auth-back-home">
              <ArrowLeft size={14}/> Home
            </Link>
            {step !== "phone" && (
              <button type="button" onClick={()=>{setStep("phone"); setOtp("");}} className="text-sm text-muted-foreground hover:text-primary" data-testid="auth-back-phone">
                ← Use a different sign-in
              </button>
            )}
          </div>

          {step === "adminLegacy" ? (
            <form onSubmit={handleAdminLogin} className="space-y-5" data-testid="admin-legacy-form">
              <div>
                <h1 className="heading text-3xl font-bold">Admin sign in</h1>
                <p className="text-sm text-muted-foreground mt-1">Use password, or sign in with your registered admin mobile via OTP.</p>
              </div>
              <div><Label>Email</Label><Input type="email" value={adminEmail} onChange={e=>setAdminEmail(e.target.value)} required data-testid="admin-email"/></div>
              <div><Label>Password</Label><Input type="password" value={adminPw} onChange={e=>setAdminPw(e.target.value)} required data-testid="admin-pw"/></div>
              <Button type="submit" disabled={busy} className="w-full rounded-full h-12 bg-primary hover:bg-primary/90" data-testid="admin-submit">
                {busy ? "Signing in..." : "Sign in with password"}
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"/></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground tracking-widest">or</span></div>
              </div>
              <Button type="button" variant="outline" className="w-full rounded-full h-12" onClick={()=>setStep("phone")} data-testid="admin-back-personas">
                ← Back to OTP login
              </Button>
            </form>
          ) : step === "phone" ? (
            <div className="space-y-5">
              <div>
                <h1 className="heading text-3xl font-bold">Welcome</h1>
                <p className="text-sm text-muted-foreground mt-1">Sign in with your mobile number — we&rsquo;ll route you to the right place.</p>
              </div>

              <GoogleButton onClick={handleGoogle} busy={busy}/>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"/></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground tracking-widest">or</span></div>
              </div>

              <form onSubmit={handleSendOtp} className="space-y-4" data-testid="phone-form">
                <div>
                  <Label htmlFor="phone">Mobile number</Label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                    <Input
                      id="phone" type="tel"
                      autoComplete="off" autoCorrect="off" spellCheck={false} name="delco-mobile"
                      data-lpignore="true" data-form-type="other"
                      value={phone}
                      onChange={(e)=>setPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      className="pl-9" required
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">We&apos;ll send you a 6-digit OTP</p>
                </div>

                <Button type="submit" disabled={busy} className="w-full rounded-full h-12 bg-primary hover:bg-primary/90" data-testid="send-otp-btn">
                  {busy ? "Sending OTP..." : "Send OTP"}
                </Button>
              </form>

              <div className="text-center pt-2">
                <button type="button" onClick={()=>setStep("adminLegacy")} className="text-xs text-muted-foreground hover:text-primary" data-testid="admin-login-link">
                  Admin? Sign in with email &amp; password →
                </button>
              </div>
            </div>
          ) : step === "otp" ? (
            <form onSubmit={handleVerifyOtp} className="space-y-5" data-testid="otp-form">
              <div>
                <h1 className="heading text-3xl font-bold">Enter OTP</h1>
                <p className="text-sm text-muted-foreground mt-1">Sent to <b>{phone}</b></p>
              </div>

              <div>
                <Label>6-digit OTP</Label>
                <Input
                  type="text" inputMode="numeric" maxLength={6}
                  value={otp}
                  onChange={(e)=>setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                  required autoFocus
                  data-testid="otp-input"
                />
              </div>

              <div className="space-y-3">
                <div>
                  <Label>Full name <span className="text-muted-foreground">(new users)</span></Label>
                  <Input value={name} onChange={e=>setName(e.target.value)} placeholder="Priya Sharma" data-testid="name-input"/>
                </div>
                <div>
                  <Label>Email <span className="text-muted-foreground">(optional, for updates)</span></Label>
                  <Input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" data-testid="email-input"/>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Checkbox id="keep" checked={keepLogin} onCheckedChange={setKeepLogin} data-testid="keep-login"/>
                <Label htmlFor="keep" className="cursor-pointer m-0">Keep me logged in</Label>
              </div>

              <Button type="submit" disabled={busy || otp.length !== 6} className="w-full rounded-full h-12 bg-primary hover:bg-primary/90" data-testid="verify-otp-btn">
                {busy ? "Verifying..." : "Verify & sign in"}
              </Button>

              <div className="text-center">
                <button
                  type="button" disabled={cooldown > 0}
                  onClick={handleSendOtp}
                  className="text-sm text-muted-foreground hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="resend-otp"
                >
                  {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
                </button>
              </div>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
}
